import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { catchError, EMPTY, from, mergeMap, tap, throwError } from 'rxjs';
import { ApiConnectivityService } from '../services/api-connectivity.service';
import { API_BASE_URL } from '../../../service/proxy';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const oktaAuth = inject(OKTA_AUTH);
    const router = inject(Router);
    const connectivityService = inject(ApiConnectivityService);
    const apiBaseUrl = inject(API_BASE_URL, { optional: true }) || '';

    const accessToken = oktaAuth.getAccessToken();
    let request = req;

    // Block API requests when there is no valid session token.
    // Allow non-API requests (e.g. static assets) through unmodified.
    const isApiRequest = apiBaseUrl && req.url.startsWith(apiBaseUrl);

    if (isApiRequest && !accessToken) {
        return EMPTY;
    }

    if (accessToken) {
        request = req.clone({
            setHeaders: {
                'Authorization': `Bearer ${accessToken}`
            }
        });
    }

    return next(request).pipe(
        tap(() => {
            connectivityService.reportSuccess();
        }),
        catchError((error: HttpErrorResponse) => {
            if (error.status === 0 || error.status === 504 || error.status === 503 || !navigator.onLine) {
                connectivityService.reportFailure();
            }
            else {
                connectivityService.reportSuccess();
            }

            if (error.status !== 403) {
                return throwError(() => error);
            }

            // Only a failed authentication ends the session. A permission refusal on one call
            // is left to the page to report, and an unregistered user is routed by AuthService.
            return from(readErrorBody(error.error)).pipe(
                mergeMap((body) => {
                    const isUserNotRegistered = body?.error === 'USER_NOT_REGISTERED';
                    const isPermissionRefusal = body?.code === 'INSUFFICIENT_PERMISSIONS';

                    if (!isUserNotRegistered && !isPermissionRefusal) {
                        localStorage.removeItem('permissions');
                        localStorage.removeItem('tenants');
                        try { (oktaAuth as any)?.tokenManager?.clear?.(); } catch { /* ignore */ }
                        void router.navigate(['/']);
                    }

                    return throwError(() => error);
                })
            );
        })
    );
};

/**
 * Reads a 403 body into an object. The generated proxy requests blobs, so the body
 * usually arrives as a Blob rather than parsed JSON.
 */
async function readErrorBody(body: unknown): Promise<any> {
    try {
        const text = body instanceof Blob
            ? await body.text()
            : body;

        return typeof text === 'string'
            ? JSON.parse(text)
            : text;
    }
    catch {
        return null;
    }
}
